import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatApiError } from "@/contexts/AuthContext";
import { ArrowLeft, ClipboardList, Loader2, ShoppingCart, Truck, PackageCheck, X, Clock } from "lucide-react";

const STATUS_STYLES = {
  new:        { icon: Clock,         cls: "bg-blue-50 text-blue-700 border-blue-200" },
  processing: { icon: Loader2,       cls: "bg-amber-50 text-amber-700 border-amber-200" },
  shipped:    { icon: Truck,         cls: "bg-purple-50 text-purple-700 border-purple-200" },
  delivered:  { icon: PackageCheck,  cls: "bg-green-50 text-green-700 border-green-200" },
  cancelled:  { icon: X,             cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function MarketingMyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/marketing/my-orders");
        setOrders(data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6" data-testid="my-orders-page">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B] mb-2">
              <ArrowLeft size={14} /> Back
            </Link>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-2">
              <ClipboardList size={12} /> Marketing orders
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[#0B132B] tracking-tight leading-none mt-1">
              My orders
            </h1>
          </div>
          <Link to="/marketing/materials">
            <Button size="sm" className="h-9 rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90" data-testid="my-orders-shop-btn">
              <ShoppingCart size={12} className="mr-1.5" /> Order more materials
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#6C757D]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading orders…</div>
        ) : orders.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-[#E9ECEF]" data-testid="my-orders-empty">
            <ClipboardList size={32} className="mx-auto text-[#6C757D] mb-2" />
            <div className="font-bold text-[#0B132B]">No orders yet</div>
            <div className="text-sm text-[#6C757D] mt-1">Head to the catalog to order brochures and rate cards.</div>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="my-orders-list">
            {orders.map((o) => {
              const { icon: Icon, cls } = STATUS_STYLES[o.status] || STATUS_STYLES.new;
              return (
                <Card key={o.id} className="p-4 border-[#E9ECEF] rounded-sm bg-white" data-testid={`my-order-${o.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider font-bold text-[#D4AF37]">Order</div>
                      <div className="font-bold text-lg text-[#0B132B] font-mono">{o.order_number}</div>
                      <div className="text-xs text-[#6C757D] mt-1">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <Badge className={`rounded-sm border ${cls} inline-flex items-center gap-1.5 px-3 py-1`} data-testid={`order-status-${o.id}`}>
                      <Icon size={12} /> {o.status_label}
                    </Badge>
                  </div>

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-[11px] font-bold uppercase text-[#6C757D] mb-1">Items</div>
                      <ul className="space-y-1">
                        {o.items.map((it, idx) => (
                          <li key={idx} className="text-sm text-[#0B132B]">
                            <b>{it.qty}×</b> {it.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase text-[#6C757D] mb-1">Ship to</div>
                      <div className="text-sm text-[#0B132B] leading-relaxed">
                        {o.ship_to?.name}{o.ship_to?.company && <><br />{o.ship_to.company}</>}<br />
                        {o.ship_to?.address1}{o.ship_to?.address2 && <>, {o.ship_to.address2}</>}<br />
                        {o.ship_to?.city}, {o.ship_to?.state} {o.ship_to?.zip}
                      </div>
                    </div>
                  </div>

                  {o.tracking_number && (
                    <div className="mt-3 pt-3 border-t border-[#F1F3F5] text-sm">
                      <span className="text-[11px] font-bold uppercase text-[#6C757D]">Shipped via:</span>{" "}
                      {o.carrier && <b className="text-[#0B132B]">{o.carrier}</b>}
                      {o.carrier && " · "}
                      <span className="text-[11px] font-bold uppercase text-[#6C757D]">Tracking:</span>{" "}
                      {o.tracking_url ? (
                        <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[#0B132B] font-mono font-bold underline">
                          {o.tracking_number}
                        </a>
                      ) : (
                        <span className="text-[#0B132B] font-mono font-bold">{o.tracking_number}</span>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
